import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Components
import NotaSpeseForm from "./NotaSpeseForm";
import {
  SchedaRow,
  NotaSpesaItem,
  ExpensesSummary,
  ShareModal,
  DeleteConfirmModal,
  useExpencers,
  useSchedaShare,
  useModalState,
  useTitleEdit,
} from "./shared";

// Redux actions
import {
  getPersonalSchedaSpese,
  getSharedSchedaSpese,
  deleteSchedaSpese,
  hideSchedaSpese,
  reset,
} from "../features/schedaSpese/schedaSpeseSlice";
import { deleteNotaSpese } from "../features/notaSpese/notaSpeseSlice";
import Form from "react-bootstrap/Form";

function SingleSchedaNew({ scheda, showsharedOrPersonal }) {
    const listScope = showsharedOrPersonal === "personal" ? "personal" : "shared";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { categorie: categories } = useSelector((state) => state.categorie);

  // Custom hooks
  const { modalState, handleShow, handleClose } = useModalState();
  const { expencersWithTotals, maxExpencer } = useExpencers(scheda);
  
  const onRefreshList = async () => {
    if (listScope === "personal") {
      await dispatch(getPersonalSchedaSpese()).unwrap();
    } else {
      await dispatch(getSharedSchedaSpese()).unwrap();
    }
  };

  const {
    titolo,
    erroLength,
    isEditing,
    onLongPress,
    onChange,
    cancelEdit,
    updateSchedataTitolo,
  } = useTitleEdit(scheda, onRefreshList);

  const {
    utentiList,
    isFormModified,
    removeSharedUser,
    changeUserRole,
    getPendingRole,
    addEmail,
    validateEmail,
    removeEmail,
    resetForm,
    handleSubmit: editSharedUserSubmit,
  } = useSchedaShare(scheda, onRefreshList, () => handleClose("shareModal"));

  // Local state
  const [notaSpesaToEdit, setNotaSpesaToEdit] = useState(null);
  const [refSchedaId, SetRefSchedaId] = useState(null);
  const [notifyOwner, setNotifyOwner] = useState(false);

  // Handlers
  const handleModalClose = (modalType) => {
    handleClose(modalType);
    resetForm();
    if (modalType !== "editNotaModal") setNotaSpesaToEdit(null);
    if (modalType === "deleteNotaModal") setNotaSpesaToEdit(null);
  };

  const editNota = (notaSpesa) => {
    handleShow("editNotaModal");
    setNotaSpesaToEdit(notaSpesa);
  };

  const goToDettagolioScheda = (schedaID) => {
    navigate(`/note-spese/${listScope}/${schedaID}`);
    dispatch(reset());
  };

  const closeCreateEditModal = (schedaID) => {
    handleClose("editNotaModal");
    handleShow("deleteNotaModal");
    SetRefSchedaId(schedaID);
  };

  const deleteNotaSpeseAction = async (notaId) => {
    const body = {
      notaId,
      schedaId: refSchedaId,
    };

    try {
      const response = await dispatch(deleteNotaSpese(body)).unwrap();
      await onRefreshList();
      handleModalClose("deleteNotaModal");
      toast.success(response.message || "Note succesfull deleted");
    } catch (error) {
      console.log("Error Response:", error);
      toast.error(`Error deleting note: ${error.message}`);
    }
  };

  const hideSchedaAction = async () => {
    try {
      await dispatch(hideSchedaSpese({ id: scheda._id, notifyOwner })).unwrap();
      toast.success("Scheda hidden from your list");
    } catch (error) {
      toast.error(error?.message || error || "Error hiding scheda");
    } finally {
      setNotifyOwner(false);
      handleModalClose("hideModal");
    }
  };

  return (
    <>
      <div>
        <div className="h-100 pb-2 mb-2">
          {/* Header with title and actions */}
          <SchedaRow
            className="mb-2"
            scheda={scheda}
            isEditing={isEditing}
            titolo={titolo}
            erroLength={erroLength}
            onLongPress={onLongPress}
            onChange={onChange}
            cancelEdit={cancelEdit}
            updateSchedataTitolo={updateSchedataTitolo}
            onShare={() => handleShow("shareModal")}
            onDelete={() => handleShow("deleteModal")}
            onHide={() => handleShow("hideModal")}
            onTitleClick={() => goToDettagolioScheda(scheda._id)}
          />

          {/* Notes list */}
          {scheda.notaSpese.length > 0 && (
            <div className="card-body px-3">
              <ul className="list-group">
                {scheda.notaSpese.map(
                  (notaSpesa, i) =>
                    notaSpesa &&
                    i < 2 && (
                      <NotaSpesaItem
                        key={notaSpesa._id}
                        notaSpesa={notaSpesa}
                        user={user}
                        categories={categories}
                        onEdit={editNota}
                      />
                    )
                )}
              </ul>
              {scheda.notaSpese.length > 2 && (
                <div className="row pt-0 px-3">
                  <div className="col-12 d-flex">
                    <div className="d-flex align-items-center">
                      <div className="">
                        <small className="text-dark-emphasis">
                          Showing 2 of {scheda.notaSpese.length}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="row pt-0 p-3 gy-2">
            <div className="col-12">
              <div
                className="dashboard-btn-primary w-100 mt-3"
                onClick={() => handleShow("creaNotaModal")}
              >
                <p className="mb-0">Add note</p>
              </div>
            </div>
            {scheda.notaSpese.length > 0 && (
              <div className="col-12">
                <div
                  className="dashboard-btn-secondary w-100"
                  onClick={() => goToDettagolioScheda(scheda._id)}
                >
                  <p className="mb-0">See all</p>
                </div>
              </div>
            )}
          </div>

          {/* Expenses summary */}
          {scheda.notaSpese.length > 0 && (
            <div className="px-3 pb-3">
              <ExpensesSummary
                scheda={scheda}
                expencersWithTotals={expencersWithTotals}
                maxExpencer={maxExpencer}
                variant="compact"
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <div className="row">
        {/* Create Note Modal */}
        <Modal
          show={modalState.creaNotaModal}
          onHide={() => handleModalClose("creaNotaModal")}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <b>Create note in {scheda.titolo}</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <NotaSpeseForm
              onSuccess={handleModalClose}
              schedaId={scheda._id}
            />
          </Modal.Body>
        </Modal>

        {/* Edit Note Modal */}
        <Modal
          show={modalState.editNotaModal}
          onHide={() => handleModalClose("editNotaModal")}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <b>Edit note in {scheda.titolo}</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <NotaSpeseForm
              onSuccess={handleModalClose}
              schedaId={scheda._id}
              notaToEdit={notaSpesaToEdit}
              beforeDelete={() => closeCreateEditModal(scheda._id)}
            />
          </Modal.Body>
        </Modal>

        {/* Share Modal */}
        <ShareModal
          show={modalState.shareModal}
          onHide={() => handleModalClose("shareModal")}
          scheda={scheda}
          user={user}
          utentiList={utentiList}
          isFormModified={isFormModified}
          onAddEmail={addEmail}
          onValidateEmail={validateEmail}
          onRemoveEmail={removeEmail}
          onRemoveSharedUser={removeSharedUser}
          onChangeUserRole={changeUserRole}
          getPendingRole={getPendingRole}
          onSubmit={editSharedUserSubmit}
          onClose={() => handleModalClose("shareModal")}
        />

        {/* Delete Scheda Modal */}
        <DeleteConfirmModal
          show={modalState.deleteModal}
          onHide={() => handleModalClose("deleteModal")}
          title={scheda.titolo}
          itemName={scheda.titolo}
          itemType="scheda"
          onConfirm={() => dispatch(deleteSchedaSpese(scheda._id))}
          onCancel={() => handleModalClose("deleteModal")}
        />

        {/* Delete Note Modal */}
        <DeleteConfirmModal
          show={modalState.deleteNotaModal}
          onHide={() => handleModalClose("deleteNotaModal")}
          title={notaSpesaToEdit?.testo}
          itemName={notaSpesaToEdit?.testo}
          itemType="note"
          onConfirm={() => deleteNotaSpeseAction(notaSpesaToEdit._id)}
          onCancel={() => handleModalClose("deleteNotaModal")}
        />

        {/* Hide Scheda Modal */}
        <Modal
          show={modalState.hideModal}
          onHide={() => handleModalClose("hideModal")}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <b>Hide "{scheda.titolo}"?</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              This only hides it from your own shared list — everyone else
              keeps seeing it, and you can restore it anytime.
            </p>
            <Form.Check
              type="checkbox"
              id="notify-owner-check"
              label="Notify the owner that I hid this scheda"
              checked={notifyOwner}
              onChange={(e) => setNotifyOwner(e.target.checked)}
              className="mb-2"
            />
            <div className="row mt-4 mb-3">
              <div className="col-4">
                <button
                  className="dashboard-btn-secondary btn-sm mb-0 w-100"
                  onClick={() => {
                    setNotifyOwner(false);
                    handleModalClose("hideModal");
                  }}
                >
                  <p className="mb-0">cancel</p>
                </button>
              </div>
              <div className="col-8">
                <button
                  className="dashboard-btn-primary w-100"
                  onClick={hideSchedaAction}
                >
                  Hide
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}

export default SingleSchedaNew;
